import React from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import LegalFooter from './shared/LegalFooter';
import { 
  FileText,
  User,
  Shield,
  CheckCircle,
  AlertTriangle,
  Mail,
  Scale,
  Clock,
  CreditCard,
  Users,
  Zap,
  Globe,
  Settings
} from 'lucide-react';

const TermsPage = () => {
  const sections = [
    {
      id: "about",
      title: "1. About Smart AI Solutions",
      icon: FileText,
      content: "Smart AI Solutions provides AI-powered voice and knowledge solutions for modern businesses, including VioQbase, VioReceptionist, VioConcierge, VioCoach, and HR Consultancy services. Our services are designed to drive efficiency, enhance customer excellence, and transform how organizations operate through intelligent automation."
    },
    {
      id: "acceptance",
      title: "2. Acceptance of Terms",
      icon: CheckCircle,
      content: "By accessing or using our website, creating an account, or subscribing to any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, and any other legal notices published by us."
    },
    {
      id: "eligibility",
      title: "3. Eligibility",
      icon: User,
      content: "You must be at least 18 years old and have the legal capacity to enter into a binding agreement to use our website and services."
    },
    {
      id: "account",
      title: "4. Account Registration",
      icon: Users,
      content: "Some features of VioQbase may require account registration. You agree to provide accurate, current, and complete information and to keep this information up to date. You are responsible for maintaining the confidentiality of your account and for all activities that occur under your account."
    },
    {
      id: "service-use",
      title: "5. Use of the Service",
      icon: Settings,
      content: "You agree to use Smart AI Solutions services only for lawful purposes and in accordance with these Terms. You agree not to:",
      list: [
        "Misuse, reverse-engineer, or attempt to access the source code of our software",
        "Upload or distribute malicious software or content",
        "Use our services to store or transmit infringing, defamatory, or unlawful content",
        "Access or attempt to access another user's account or data"
      ]
    },
    {
      id: "intellectual-property",
      title: "6. Intellectual Property",
      icon: Shield,
      content: "All content, trademarks, software, and services provided by Smart AI Solutions are owned or licensed by us and are protected by copyright, trademark, and other intellectual property laws. You may not use our content without our express written permission."
    },
    {
      id: "service-availability",
      title: "7. Service Availability",
      icon: Globe,
      content: "We strive to maintain uninterrupted access to our services, but we do not guarantee that our platforms will always be available or error-free. We may suspend, withdraw, or modify services at any time without notice."
    },
    {
      id: "subscription-payment",
      title: "8. Subscription & Payment Terms",
      icon: CreditCard,
      content: "Certain features of Smart AI Solutions services may require a paid subscription. If applicable, pricing, billing cycles, and cancellation policies will be provided separately through our pricing page or subscription agreement."
    },
    {
      id: "termination",
      title: "9. Termination",
      icon: AlertTriangle,
      content: "We may suspend or terminate your access to Smart AI Solutions services if you violate these Terms or engage in any behaviour that we consider harmful to our platforms or other users. You may cancel your account at any time by contacting support."
    },
    {
      id: "limitation-liability",
      title: "10. Limitation of Liability",
      icon: Shield,
      content: "To the maximum extent permitted by law, Smart AI Solutions and its affiliates shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of our website or services."
    },
    {
      id: "disclaimer",
      title: "11. Disclaimer",
      icon: AlertTriangle,
      content: "Smart AI Solutions services are provided \"as is\" and \"as available.\" We make no warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement."
    },
    {
      id: "governing-law",
      title: "12. Governing Law",
      icon: Scale,
      content: "These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising out of or related to these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales."
    },
    {
      id: "changes",
      title: "13. Changes to These Terms",
      icon: Clock,
      content: "We may update these Terms from time to time. When we do, we will revise the \"Effective Date\" at the top of this page. Continued use of the site after changes are made constitutes your acceptance of the new Terms."
    },
    {
      id: "contact",
      title: "14. Contact Us",
      icon: Mail,
      content: "If you have any questions about these Terms, please contact us at:",
      footer: "Smart AI Solutions Ltd\nEmail: info@smartaisolutions.ai\nAddress: 124 City Road, London EC1V 2NX"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                <Scale className="h-10 w-10 text-[#ff7a1f]" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Terms of <span className="text-[#ff7a1f]">Service</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed mb-8">
              Welcome to Smart AI Solutions. By accessing or using our website or services, you agree to be bound by these Terms of Service.
            </p>
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <Clock className="h-5 w-5 text-[#ff7a1f]" />
              <span className="text-blue-200">Effective Date: 21st January 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl bg-white">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center justify-center space-x-3">
                  <FileText className="h-6 w-6 text-[#ff7a1f]" />
                  <span>Agreement Overview</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-lg leading-relaxed">
                  If you do not agree with these Terms, please do not use our website or services. These Terms govern your access to and use of all Smart AI Solutions services and products provided by Smart AI Solutions Ltd.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto space-y-8">
            {sections.map((section, index) => (
              <Card key={section.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold text-[#112d56] flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-lg flex items-center justify-center">
                      <section.icon className="h-5 w-5 text-[#ff7a1f]" />
                    </div>
                    <span>{section.title}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    {section.content}
                  </p>
                  
                  {section.list && (
                    <ul className="space-y-2 ml-4">
                      {section.list.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-[#ff7a1f] rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.footer && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-700 font-medium whitespace-pre-line">
                        {section.footer}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
              Questions About Our <span className="text-[#ff7a1f]">Terms</span>?
            </h2>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
              We're here to help clarify any questions you may have about our terms of service.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8">
              <Button 
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-12 py-6 text-xl rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
                onClick={() => window.location.href = 'mailto:info@smartaisolutions.ai'}
              >
                <Mail className="mr-3 h-6 w-6" />
                Contact Us About Terms
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default TermsPage;